import { LitElement, html, css } from "lit";
import { customElement, property } from "lit/decorators.js";
import { shared } from "../styles.js";
import type { RequiresLabels } from "../lib/derive-requires.js";

/**
 * requires-panel renders the binding contract a consumer must supply (R9): the
 * datasets (with required columns), derived from the version document's
 * registry.requires.* labels, and params (type/default/options/description),
 * derived from the document's native metadata.params.
 */
@customElement("requires-panel")
export class RequiresPanel extends LitElement {
  static override styles = [
    shared,
    css`
      .item {
        padding: 6px 0;
        border-bottom: 1px solid var(--border);
      }
      .item:last-child {
        border-bottom: 0;
      }
      .nm {
        font-family: var(--mono);
        font-weight: 600;
      }
      .detail {
        color: var(--muted);
        font-size: var(--fs-2);
      }
      h4 {
        margin: var(--sp-3) 0 var(--sp-1);
        font-size: var(--fs-1);
        text-transform: uppercase;
        color: var(--muted);
      }
    `,
  ];

  @property({ attribute: false }) requires: RequiresLabels = { datasets: [], params: [] };

  override render() {
    const { datasets, params } = this.requires;
    if (!datasets.length && !params.length) {
      return html`<p class="muted">No datasets or params required.</p>`;
    }
    return html`
      ${datasets.length
        ? html`<h4>Datasets</h4>
            ${datasets.map(
              (d) => html`<div class="item">
                <span class="nm">${d.name}</span>
                ${d.columns ? html`<span class="detail"> — columns: ${d.columns}</span>` : ""}
                ${d.description ? html`<div class="detail">${d.description}</div>` : ""}
              </div>`,
            )}`
        : ""}
      ${params.length
        ? html`<h4>Params</h4>
            ${params.map(
              (p) => html`<div class="item">
                <span class="nm">${p.name}</span>
                <span class="detail">
                  ${[
                    p.type,
                    p.required ? "required" : "",
                    p.default ? `default ${p.default}` : "",
                    p.options ? `options ${p.options}` : "",
                    p.min ? `min ${p.min}` : "",
                    p.max ? `max ${p.max}` : "",
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>
                ${p.description ? html`<div class="detail">${p.description}</div>` : ""}
              </div>`,
            )}`
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "requires-panel": RequiresPanel;
  }
}
